import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { queryOptions } from "@tanstack/react-query";
import type { Database } from "@/integrations/supabase/types";
import { PAGE_SOURCES, SOURCES, type Source, type SourceKind } from "@/data/sources";

export type PageSourcesData = {
  intro_de: string;
  intro_en: string;
  openClaims_de: string[];
  openClaims_en: string[];
  sources: Source[];
};

export type InteractionSource = Source & { note_de: string | null; note_en: string | null };

const KINDS: SourceKind[] = [
  "leitlinie",
  "behoerde",
  "fachinfo",
  "monitoring",
  "sekundaerliteratur",
  "primaerliteratur",
];

function publicClient() {
  return createClient<Database>(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
  );
}

type SourceRow = {
  key: string;
  title: string;
  publisher: string | null;
  year: string | null;
  kind: string;
  url: string | null;
  backs_de: string | null;
  backs_en: string | null;
  caveat_de: string | null;
  caveat_en: string | null;
};

function normalizeSource(r: SourceRow): Source {
  const kind = (KINDS as string[]).includes(r.kind) ? (r.kind as SourceKind) : "sekundaerliteratur";
  return {
    id: r.key,
    title: r.title ?? "",
    publisher: r.publisher ?? "",
    year: r.year ?? "",
    kind,
    url: r.url ?? "",
    backs_de: r.backs_de ?? "",
    backs_en: r.backs_en ?? "",
    ...(r.caveat_de ? { caveat_de: r.caveat_de } : {}),
    ...(r.caveat_en ? { caveat_en: r.caveat_en } : {}),
  };
}

export function fallbackPageSources(path: string): PageSourcesData | null {
  const entry = PAGE_SOURCES[path];
  if (!entry) return null;
  return {
    intro_de: entry.intro_de,
    intro_en: entry.intro_en,
    openClaims_de: entry.openClaims_de,
    openClaims_en: entry.openClaims_en,
    sources: entry.sourceIds.map((id) => SOURCES[id]).filter(Boolean) as Source[],
  };
}

export const getPageSourcesPublic = createServerFn({ method: "GET" })
  .inputValidator((i: unknown) => {
    const path = (i as { path?: unknown })?.path;
    if (typeof path !== "string" || !path) throw new Error("Ungueltiger path");
    return { path };
  })
  .handler(async ({ data }): Promise<PageSourcesData | null> => {
    try {
      const sb = publicClient();
      const { data: page, error } = await sb
        .from("page_sources")
        .select("id, intro_de, intro_en, open_claims_de, open_claims_en")
        .eq("path", data.path)
        .maybeSingle();
      if (error) {
        console.error("[public-sources] getPageSourcesPublic DB error", error.message);
        return fallbackPageSources(data.path);
      }
      if (!page) return fallbackPageSources(data.path);

      const { data: links, error: linkError } = await sb
        .from("page_source_links")
        .select(
          "sort_order, sources ( key, title, publisher, year, kind, url, backs_de, backs_en, caveat_de, caveat_en, published )",
        )
        .eq("page_source_id", page.id)
        .order("sort_order", { ascending: true });
      if (linkError) {
        console.error("[public-sources] page_source_links DB error", linkError.message);
        return fallbackPageSources(data.path);
      }

      const sources = (links ?? [])
        .map((l) => l.sources as (SourceRow & { published: boolean }) | null)
        .filter((s): s is SourceRow & { published: boolean } => Boolean(s) && s!.published)
        .map(normalizeSource);

      return {
        intro_de: page.intro_de ?? "",
        intro_en: page.intro_en ?? "",
        openClaims_de: page.open_claims_de ?? [],
        openClaims_en: page.open_claims_en ?? [],
        sources,
      };
    } catch (e) {
      console.error("[public-sources] getPageSourcesPublic threw", e instanceof Error ? e.message : e);
      return fallbackPageSources(data.path);
    }
  });

export const listInteractionSourcesPublic = createServerFn({ method: "GET" })
  .inputValidator((i: unknown) => {
    const id = (i as { interactionId?: unknown })?.interactionId;
    if (typeof id !== "number" || !Number.isFinite(id)) throw new Error("Ungueltige interactionId");
    return { interactionId: id };
  })
  .handler(async ({ data }): Promise<InteractionSource[]> => {
    try {
      const sb = publicClient();
      const { data: rows, error } = await sb
        .from("interaction_sources")
        .select(
          "note_de, note_en, sort_order, sources ( key, title, publisher, year, kind, url, backs_de, backs_en, caveat_de, caveat_en, published )",
        )
        .eq("interaction_id", data.interactionId)
        .order("sort_order", { ascending: true });
      if (error) {
        console.error("[public-sources] listInteractionSourcesPublic DB error", error.message);
        return [];
      }
      return (rows ?? [])
        .filter((r) => r.sources && (r.sources as { published: boolean }).published)
        .map((r) => ({
          ...normalizeSource(r.sources as unknown as SourceRow),
          note_de: r.note_de,
          note_en: r.note_en,
        }));
    } catch (e) {
      console.error("[public-sources] listInteractionSourcesPublic threw", e instanceof Error ? e.message : e);
      return [];
    }
  });

export const pageSourcesQuery = (path: string) =>
  queryOptions({
    queryKey: ["page-sources", path],
    queryFn: () => getPageSourcesPublic({ data: { path } }),
    staleTime: 5 * 60 * 1000,
  });

export const interactionSourcesQuery = (interactionId: number | null) =>
  queryOptions({
    queryKey: ["interaction-sources", interactionId],
    queryFn: () =>
      interactionId == null
        ? Promise.resolve([] as InteractionSource[])
        : listInteractionSourcesPublic({ data: { interactionId } }),
    enabled: interactionId != null,
    staleTime: 5 * 60 * 1000,
  });
